import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { Link } from './Link';
import {
  buildConsentState,
  JC_COOKIE_CONSENT_OPEN_EVENT,
  parseConsentFromCookieString,
  runAnalyticsConsentBootstrap,
  setConsentCookieInBrowser,
} from '../utils/cookie-consent';

function isBackofficePath(pathname: string): boolean {
  return pathname.split('?')[0].includes('/backoffice');
}

export function CookieConsentBanner() {
  const intl = useIntl();
  const pageContext = usePageContext();
  const pathname =
    (pageContext as { urlPathname?: string })?.urlPathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  /** null = unknown (before mount); true = jc_consent_v1 present; false = no choice yet (show banner). */
  const [hasStoredChoice, setHasStoredChoice] = useState<boolean | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [analyticsToggle, setAnalyticsToggle] = useState(false);

  const refreshFromCookie = useCallback(() => {
    if (typeof document === 'undefined') return;
    const parsed = parseConsentFromCookieString(document.cookie);
    setHasStoredChoice(parsed !== null);
    setAnalyticsToggle(parsed?.analytics === true);
  }, []);

  useLayoutEffect(() => {
    refreshFromCookie();
  }, [refreshFromCookie]);

  useEffect(() => {
    const onOpen = () => {
      refreshFromCookie();
      setAnalyticsToggle(parseConsentFromCookieString(document.cookie)?.analytics === true);
      setModalOpen(true);
    };
    document.addEventListener(JC_COOKIE_CONSENT_OPEN_EVENT, onOpen);
    return () => document.removeEventListener(JC_COOKIE_CONSENT_OPEN_EVENT, onOpen);
  }, [refreshFromCookie]);

  const persist = useCallback(
    (analytics: boolean) => {
      const state = buildConsentState(analytics);
      setConsentCookieInBrowser(state, { secure: import.meta.env.PROD });
      setHasStoredChoice(true);
      setModalOpen(false);
      if (analytics) {
        runAnalyticsConsentBootstrap();
      } else if (typeof window !== 'undefined' && (window as unknown as { gtag_initialized?: boolean }).gtag_initialized) {
        window.location.reload();
        return;
      }
      refreshFromCookie();
    },
    [refreshFromCookie],
  );

  const handleAccept = () => persist(true);
  const handleReject = () => persist(false);

  const handleSaveModal = () => persist(analyticsToggle);

  if (isBackofficePath(pathname)) {
    return null;
  }

  const showBanner = hasStoredChoice === false;

  return (
    <>
      {showBanner ? (
        <div
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
          className="fixed bottom-0 left-0 right-0 z-[100] border-t border-[#DDD5C8] dark:border-[#4A3520] bg-[#F8F5F0] dark:bg-[#170F07] shadow-[0_-4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_24px_rgba(0,0,0,0.4)]"
        >
          <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 sm:py-5">
            <h3
              id="cookie-consent-title"
              className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-2"
            >
              <FormattedMessage id="cookie.consent.title" defaultMessage="Cookies and privacy" />
            </h3>
            <p id="cookie-consent-desc" className="text-sm text-[#3D3529] dark:text-[#C4A882] mb-4 leading-relaxed">
              <FormattedMessage
                id="cookie.consent.banner"
                defaultMessage="We use essential cookies to run JobCrawls. If you accept, we also use Google Analytics and Algolia analytics to improve search and understand traffic. You can change this anytime via Cookie settings in the footer. Read more in our Privacy Policy."
              />
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 sm:items-center">
              <button
                type="button"
                onClick={handleAccept}
                className="cursor-pointer inline-flex justify-center items-center rounded-xl px-4 py-2.5 text-sm font-medium bg-[#3B5E45] text-white shadow-sm transition-all duration-150 ease-out hover:bg-[#2d4836] hover:shadow-md hover:brightness-[1.03] active:scale-[0.98] dark:bg-[#98CFA8] dark:text-[#170F07] dark:hover:bg-[#b6e4c4] dark:hover:shadow-md dark:hover:brightness-[1.05]"
              >
                <FormattedMessage id="cookie.consent.accept" defaultMessage="Accept analytics" />
              </button>
              <button
                type="button"
                onClick={handleReject}
                className="cursor-pointer inline-flex justify-center items-center rounded-xl px-4 py-2.5 text-sm font-medium border border-[#DDD5C8] dark:border-[#4A3520] text-[#1E1A14] dark:text-[#F2EADF] bg-white dark:bg-[#1F1509] transition-all duration-150 ease-out hover:bg-[#F2EADF] hover:border-[#C4A882]/70 dark:hover:bg-[#2A1D0F] dark:hover:border-[#6E5A48] active:scale-[0.98]"
              >
                <FormattedMessage id="cookie.consent.reject" defaultMessage="Reject non-essential" />
              </button>
              <button
                type="button"
                onClick={() => {
                  refreshFromCookie();
                  setAnalyticsToggle(parseConsentFromCookieString(document.cookie)?.analytics === true);
                  setModalOpen(true);
                }}
                className="cursor-pointer inline-flex justify-center items-center rounded-lg px-2 py-1 -mx-2 -my-1 text-sm font-medium text-[#3B5E45] dark:text-[#98CFA8] transition-colors duration-150 ease-out hover:bg-[#C8DDCE]/50 hover:text-[#2d4836] hover:underline dark:hover:bg-[#1A2E22]/80 dark:hover:text-[#B6D4BC] active:scale-[0.98] sm:ml-1"
              >
                <FormattedMessage id="cookie.consent.manage" defaultMessage="Manage preferences" />
              </button>
              <Link
                href="/privacy"
                locale={intl.locale}
                className="cursor-pointer inline-flex justify-center items-center rounded-lg px-2 py-1 -mx-2 -my-1 text-sm text-[#5A4A3A] dark:text-[#B8A48C] transition-colors duration-150 ease-out hover:bg-[#F2EADF]/80 hover:text-[#3B5E45] dark:hover:bg-[#2A1D0F]/80 dark:hover:text-[#98CFA8] sm:ml-auto"
              >
                <FormattedMessage id="cookie.consent.privacyLink" defaultMessage="Privacy Policy" />
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div
          className="fixed inset-0 z-[110] flex cursor-pointer items-end sm:items-center justify-center p-4 bg-black/40 transition-colors duration-200 ease-out hover:bg-black/45 dark:bg-black/60 dark:hover:bg-black/65"
          role="presentation"
          onClick={() => setModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-modal-title"
            className="w-full max-w-lg cursor-default rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] shadow-xl p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cookie-modal-title" className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-4">
              <FormattedMessage id="cookie.consent.modalTitle" defaultMessage="Cookie preferences" />
            </h3>
            <div className="rounded-xl border border-[#DDD5C8] dark:border-[#4A3520] p-4 mb-4 transition-colors duration-150 ease-out hover:border-[#C4A882]/60 hover:bg-[#F8F5F0]/80 dark:hover:border-[#6E5A48] dark:hover:bg-[#170F07]/50">
              <label className="flex gap-3 cursor-pointer items-start transition-opacity duration-150 hover:opacity-95">
                <input
                  type="checkbox"
                  checked={analyticsToggle}
                  onChange={(e) => setAnalyticsToggle(e.target.checked)}
                  className="mt-1 h-4 w-4 cursor-pointer rounded border-[#DDD5C8] text-[#3B5E45] transition-shadow duration-150 focus:ring-[#3B5E45] hover:ring-2 hover:ring-[#3B5E45]/25 dark:border-[#4A3520] dark:hover:ring-[#98CFA8]/30"
                />
                <span>
                  <span className="block text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF]">
                    <FormattedMessage id="cookie.consent.analyticsTitle" defaultMessage="Analytics" />
                  </span>
                  <span className="block text-xs text-[#5A4A3A] dark:text-[#B8A48C] mt-1 leading-relaxed">
                    <FormattedMessage
                      id="cookie.consent.analyticsBody"
                      defaultMessage="Google Analytics 4 and Algolia Insights help us measure searches and improve results. Optional."
                    />
                  </span>
                </span>
              </label>
            </div>
            <p className="text-xs text-[#5A4A3A] dark:text-[#B8A48C] mb-4">
              <FormattedMessage
                id="cookie.consent.necessaryNote"
                defaultMessage="Essential cookies (session, preferences, security) are always used so the site works."
              />
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="cursor-pointer inline-flex justify-center rounded-xl px-4 py-2 text-sm border border-[#DDD5C8] dark:border-[#4A3520] text-[#1E1A14] dark:text-[#F2EADF] transition-all duration-150 ease-out hover:bg-[#F2EADF] hover:border-[#C4A882]/70 dark:hover:bg-[#2A1D0F] dark:hover:border-[#6E5A48] active:scale-[0.98]"
              >
                <FormattedMessage id="cookie.consent.cancel" defaultMessage="Cancel" />
              </button>
              <button
                type="button"
                onClick={handleSaveModal}
                className="cursor-pointer inline-flex justify-center rounded-xl px-4 py-2 text-sm font-medium bg-[#3B5E45] text-white shadow-sm transition-all duration-150 ease-out hover:bg-[#2d4836] hover:shadow-md hover:brightness-[1.03] active:scale-[0.98] dark:bg-[#98CFA8] dark:text-[#170F07] dark:hover:bg-[#b6e4c4] dark:hover:shadow-md dark:hover:brightness-[1.05]"
              >
                <FormattedMessage id="cookie.consent.save" defaultMessage="Save choices" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
